import * as THREE from "three";

let scene, camera, renderer, model;
let spherical = { radius: 25, theta: Math.PI / 4, phi: Math.PI / 4 };
let panOffset = new THREE.Vector3();
let isDragging = false;
let isPanning = false;
let prevMouse = { x: 0, y: 0 };

export function initViewer(canvas) {
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf5f5f5);

  const ambientLight = new THREE.AmbientLight(0xffffff, 10.0);
  scene.add(ambientLight);

  const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 10.0);
  scene.add(hemiLight);

  const dirLight1 = new THREE.DirectionalLight(0xffffff, 10.0);
  dirLight1.position.set(5, 10, 7);
  scene.add(dirLight1);

  const dirLight2 = new THREE.DirectionalLight(0xffffff, 10.0);
  dirLight2.position.set(-5, -5, -5);
  scene.add(dirLight2);

  camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);
  camera.position.set(20, 15, 20);
  camera.lookAt(0, 0, 0);

  renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const grid = new THREE.GridHelper(40, 40, 0x444444, 0x333333);
  scene.add(grid);

  window.addEventListener("resize", onResize);
  onResize();

  setupOrbitControls();

  renderer.render(scene, camera);
}

function updateCamera() {
  const r = spherical.radius;
  const sinPhi = Math.sin(spherical.phi);
  camera.position.set(
    r * sinPhi * Math.sin(spherical.theta),
    r * Math.cos(spherical.phi),
    r * sinPhi * Math.cos(spherical.theta)
  );
  camera.position.add(panOffset);
  camera.lookAt(panOffset);
}

function setupOrbitControls() {
  const container = document.getElementById("viewer-container");

  container.addEventListener("pointerdown", (e) => {
    if (e.target.tagName !== "CANVAS") return;
    if (e.button === 2 || e.button === 1) {
      isPanning = true;
    } else {
      isDragging = true;
    }
    prevMouse = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  });

  window.addEventListener("pointermove", (e) => {
    if (!isDragging && !isPanning) return;
    const dx = e.clientX - prevMouse.x;
    const dy = e.clientY - prevMouse.y;
    prevMouse = { x: e.clientX, y: e.clientY };

    if (isDragging) {
      spherical.theta -= dx * 0.01;
      spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi + dy * 0.01));
    } else if (isPanning) {
      const panSpeed = spherical.radius * 0.002;
      const right = new THREE.Vector3();
      const up = new THREE.Vector3();
      const dir = new THREE.Vector3().subVectors(camera.position, panOffset).normalize();
      right.crossVectors(dir, camera.up).normalize();
      up.copy(camera.up);
      panOffset.add(right.multiplyScalar(dx * panSpeed));
      panOffset.add(up.multiplyScalar(-dy * panSpeed));
    }

    updateCamera();
    renderer.render(scene, camera);
  });

  window.addEventListener("pointerup", () => {
    isDragging = false;
    isPanning = false;
  });

  container.addEventListener("wheel", (e) => {
    e.preventDefault();
    spherical.radius = Math.max(5, Math.min(200, spherical.radius * (1 + e.deltaY * 0.001)));
    updateCamera();
    renderer.render(scene, camera);
  }, { passive: false });

  container.addEventListener("contextmenu", (e) => e.preventDefault());

  let prevTouchDist = 0;

  container.addEventListener("touchstart", (e) => {
    if (e.target.tagName !== "CANVAS") return;
    e.preventDefault();
    if (e.touches.length === 1) {
      isDragging = true;
      prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      isDragging = false;
      prevTouchDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }
  }, { passive: false });

  container.addEventListener("touchmove", (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - prevMouse.x;
      const dy = e.touches[0].clientY - prevMouse.y;
      spherical.theta -= dx * 0.01;
      spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi + dy * 0.01));
      prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      updateCamera();
      renderer.render(scene, camera);
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      spherical.radius = Math.max(5, Math.min(200, spherical.radius * (prevTouchDist / dist)));
      prevTouchDist = dist;
      updateCamera();
      renderer.render(scene, camera);
    }
  }, { passive: false });

  container.addEventListener("touchend", () => {
    isDragging = false;
  });
}

function onResize() {
  const container = renderer.domElement.parentElement;
  if (!container || !renderer.domElement) return;
  const w = Math.floor(container.clientWidth);
  const h = Math.floor(container.clientHeight);
  if (w === 0 || h === 0) return;
  renderer.domElement.width = w;
  renderer.domElement.height = h;
  renderer.setSize(w, h);
  renderer.setPixelRatio(window.devicePixelRatio);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.render(scene, camera);
}

window.addEventListener("resize", onResize);

export function loadModel(text) {
  return new Promise((resolve, reject) => {
    const group = new THREE.Group();

    function extractBalancedBlocks(text, blockName) {
      const blocks = [];
      const regex = new RegExp(blockName + '\\s*\\{', 'g');
      let match;
      while ((match = regex.exec(text)) !== null) {
        const start = match.index + match[0].length;
        let depth = 1;
        let pos = start;
        while (pos < text.length && depth > 0) {
          if (text[pos] === '{') depth++;
          else if (text[pos] === '}') depth--;
          pos++;
        }
        if (depth === 0) blocks.push(text.substring(start, pos - 1));
      }
      return blocks;
    }

    const ifcBlocks = extractBalancedBlocks(text, 'IndexedFaceSet');

    for (const ifcContent of ifcBlocks) {
      // Extract coord vertices
      const coordMatch = ifcContent.match(/coord\s+Coordinate\s*\{\s*point\s*\[([\s\S]*?)\]\s*\}/);
      if (!coordMatch) continue;

      const allCoords = [];
      const coordValues = coordMatch[1].match(/-?[\d.]+/g) || [];
      for (let i = 0; i < coordValues.length; i += 3) {
        allCoords.push([parseFloat(coordValues[i]), parseFloat(coordValues[i+1]), parseFloat(coordValues[i+2])]);
      }

      // Parse coordIndex into face groups (split by -1)
      const coordIndexMatch = ifcContent.match(/coordIndex\s*\[([\s\S]*?)\]/);
      if (!coordIndexMatch) continue;

      const coordGroups = [];
      let current = [];
      const ciValues = coordIndexMatch[1].match(/-?\d+/g) || [];
      for (const v of ciValues) {
        const idx = parseInt(v);
        if (idx === -1) {
          if (current.length >= 3) coordGroups.push([...current]);
          current = [];
        } else {
          current.push(idx);
        }
      }
      if (current.length >= 3) coordGroups.push([...current]);

      // Parse colorIndex
      const colorIndexMatch = ifcContent.match(/colorIndex\s*\[([\s\S]*?)\]/);
      let colorIndices = [];
      if (colorIndexMatch) {
        const values = colorIndexMatch[1].match(/\d+/g) || [];
        colorIndices = values.map(v => parseInt(v));
      }

      // Parse colors from Color block
      const colors = [];
      const colorMatch = ifcContent.match(/color\s+Color\s*\{\s*color\s*\[([\s\S]*?)\]\s*\}/);
      if (colorMatch) {
        const colorValues = colorMatch[1].match(/-?[\d.]+/g) || [];
        for (let i = 0; i < colorValues.length; i += 3) {
          colors.push([parseFloat(colorValues[i]), parseFloat(colorValues[i+1]), parseFloat(colorValues[i+2])]);
        }
      }

      // Group faces by color index
      const facesByColor = {};
      for (let i = 0; i < coordGroups.length; i++) {
        const cIdx = (colorIndices[i] !== undefined) ? colorIndices[i] : 0;
        if (!facesByColor[cIdx]) facesByColor[cIdx] = [];
        facesByColor[cIdx].push(coordGroups[i]);
      }

      // Create a mesh for each color group
      for (const [cIdxStr, faces] of Object.entries(facesByColor)) {
        const cIdx = parseInt(cIdxStr);

        const positions = [];
        const indices = [];
        const vertexMap = {};
        let nextIdx = 0;

        for (const face of faces) {
          for (const vIdx of face) {
            if (!(vIdx in vertexMap)) {
              vertexMap[vIdx] = nextIdx++;
              const pt = allCoords[vIdx] || [0, 0, 0];
              positions.push(pt[0], pt[1], pt[2]);
            }
          }
          // Triangulate quad (4 verts) -> 2 triangles
          if (face.length === 4) {
            const [a, b, c, d] = face.map(v => vertexMap[v]);
            indices.push(a, b, c, a, c, d);
          } else if (face.length === 3) {
            const [a, b, c] = face.map(v => vertexMap[v]);
            indices.push(a, b, c);
          }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        // Default pale yellow from OpenSCAD
        const baseColor = colors[cIdx] || [0.976471, 0.843137, 0.172549];
        let materialColor;

        // Replace pale yellow with blue for visibility
        if (baseColor[0] > 0.9 && baseColor[1] > 0.8 && baseColor[2] < 0.3) {
          materialColor = 0x3366aa;
        }
        else {
          // Convert sRGB 0-1 to hex to avoid Three.js color space conversion
          const r = Math.round(baseColor[0] * 255).toString(16).padStart(2, '0');
          const g = Math.round(baseColor[1] * 255).toString(16).padStart(2, '0');
          const b = Math.round(baseColor[2] * 255).toString(16).padStart(2, '0');
          materialColor = parseInt(r + g + b, 16);
        }

        const material = new THREE.MeshBasicMaterial({
          color: materialColor,
        });

        const mesh = new THREE.Mesh(geometry, material);
        group.add(mesh);
      }
    }

    if (model) scene.remove(model);
    model = group;

    // Rotate model so OpenSCAD Z-up aligns with Three.js Y-up
    model.rotation.x = -Math.PI / 2;

    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      const scale = 20 / maxDim;
      model.scale.setScalar(scale);
      const offset = center.clone().multiplyScalar(scale);
      model.position.sub(offset);
    }

    scene.add(model);
    renderer.render(scene, camera);
    resolve();
  });
}